import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, ShieldCheck, Camera, FileText, AlertTriangle, 
  CreditCard, Lock, CheckCircle, ArrowLeft, Car, 
  FileSearch, Scale, Wallet, ClipboardCheck, BookOpen,
  Users, Clock, Eye, Gavel, Receipt, Info, Calendar
} from 'lucide-react';

const HireHubProtection = () => {
  const { user, isHost } = useAuth();
  const navigate = useNavigate();

  // Protection feature cards data
  const protectionFeatures = [
    {
      id: 'bond',
      icon: Shield,
      title: 'Security Bond Protection',
      color: 'blue',
      description: 'Hosts can set a security bond amount for each vehicle. Depending on booking length and platform rules, bonds may be authorised (held) or captured from renters.',
      points: [
        'Set custom bond amounts per vehicle',
        'Bonds may be authorised or captured based on booking setup',
        'Bond funds may be used for approved damage, tolls, fuel shortfalls, cleaning, or other permitted charges',
        'Unused bond amounts are released back to renters after trip completion'
      ]
    },
    {
      id: 'inspection',
      icon: Camera,
      title: 'Inspection & Evidence Protection',
      color: 'emerald',
      description: 'Every booking includes structured pre-hire and post-hire inspections that create a comprehensive evidence trail.',
      points: [
        'Pre-hire inspections record vehicle condition before handover',
        'Post-hire inspections document condition upon return',
        'Photos, odometer readings, fuel levels, and timestamps are securely stored',
        'Evidence packs can be generated and downloaded for dispute resolution',
        'All inspection data is timestamped and linked to bookings'
      ]
    },
    {
      id: 'claims',
      icon: Scale,
      title: 'Claims & Dispute Review',
      color: 'amber',
      description: 'A structured process for handling post-hire charges and disputes with admin oversight.',
      points: [
        'Hosts can submit post-hire charges for damage, tolls, fuel, excess KMs, or cleaning',
        'Renters can review and dispute charges they disagree with',
        'Admin team reviews evidence from both parties',
        'Approved claims may be enforced through the platform\'s payment systems',
        'All claim activity is logged for transparency'
      ]
    },
    {
      id: 'payments',
      icon: CreditCard,
      title: 'Secure Payments',
      color: 'purple',
      description: 'All payments are processed securely, providing bank-level security and transparent tracking.',
      points: [
        'Payments processed securely',
        'Platform fees and host earnings tracked transparently',
        'Hosts can view detailed statements and payout history',
        'Payout schedules are configurable',
        'All financial transactions are recorded and auditable'
      ]
    },
    {
      id: 'agreements',
      icon: FileText,
      title: 'Agreement Protection',
      color: 'indigo',
      description: 'Legal agreements are generated and stored for every host and booking, creating a clear record of platform activity.',
      points: [
        'Host Service Agreement stored upon becoming a host',
        'Booking Agreements auto-generated for each confirmed booking',
        'Agreements include terms, conditions, and responsibilities',
        'PDF copies available for download at any time',
        'Creates legal record of platform activity for all parties'
      ]
    }
  ];

  // Best practices for hosts
  const hostBestPractices = [
    {
      icon: ClipboardCheck,
      title: 'Complete Pre-Hire Inspections',
      description: 'Always complete the full pre-hire checklist with clear photos before handing over your vehicle.'
    },
    {
      icon: Camera,
      title: 'Complete Post-Hire Inspections',
      description: 'Document the vehicle condition immediately upon return with timestamped photos and readings.'
    },
    {
      icon: Eye,
      title: 'Upload Clear Photos',
      description: 'Take well-lit, detailed photos of all areas including any existing damage. Quality evidence supports your claims.'
    },
    {
      icon: Car,
      title: 'Keep Vehicle Details Accurate',
      description: 'Ensure your vehicle listing, registration, and bond settings are always up to date.'
    },
    {
      icon: Shield,
      title: 'Verify Insurance Coverage',
      description: 'Ensure your insurance policy covers peer-to-peer vehicle rental before listing.'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        iconBg: 'bg-blue-100',
        iconText: 'text-blue-600',
        badge: 'bg-blue-100 text-blue-700',
        accent: 'text-blue-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
        iconText: 'text-emerald-600',
        badge: 'bg-emerald-100 text-emerald-700',
        accent: 'text-emerald-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100',
        iconText: 'text-amber-600',
        badge: 'bg-amber-100 text-amber-700',
        accent: 'text-amber-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100',
        iconText: 'text-purple-600',
        badge: 'bg-purple-100 text-purple-700',
        accent: 'text-purple-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100',
        iconText: 'text-indigo-600',
        badge: 'bg-indigo-100 text-indigo-700',
        accent: 'text-indigo-600'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#004B8D]" />
              <span className="font-heading text-lg font-semibold text-slate-900">HireHub Protection</span>
            </div>
          </div>
          <Link to="/host">
            <Button variant="outline" size="sm">
              <Car className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12" data-testid="protection-hero">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#004B8D] to-[#0066CC] shadow-lg mb-6">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            HireHub Protection
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            HireHub gives hosts clear tools to protect their vehicles, document trips, and manage disputes with evidence-backed workflows.
          </p>
          {/* Trust Language Enhancement */}
          <p className="text-sm text-slate-500 max-w-2xl mx-auto mt-3 italic">
            Your protection depends on evidence quality. Strong documentation increases claim success.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-3 py-1">
              <Camera className="w-3 h-3 mr-1" />
              Evidence-backed inspections
            </Badge>
            <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 px-3 py-1">
              <FileText className="w-3 h-3 mr-1" />
              Booking agreements stored
            </Badge>
            <Badge className="bg-purple-100 text-purple-700 border border-purple-200 px-3 py-1">
              <CreditCard className="w-3 h-3 mr-1" />
              Secure payment enforcement
            </Badge>
            <Badge className="bg-amber-100 text-amber-700 border border-amber-200 px-3 py-1">
              <Gavel className="w-3 h-3 mr-1" />
              Admin dispute review
            </Badge>
          </div>
        </div>

        {/* Protection Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="protection-features-grid">
          {protectionFeatures.map((feature) => {
            const colors = getColorClasses(feature.color);
            const IconComponent = feature.icon;
            return (
              <Card 
                key={feature.id}
                className={`${colors.border} ${colors.bg} hover:shadow-lg transition-all duration-300`}
                data-testid={`protection-card-${feature.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-6 h-6 ${colors.iconText}`} />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-lg text-slate-900">
                        {feature.title}
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 ${colors.accent} flex-shrink-0 mt-0.5`} />
                        <span className="text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* What Hosts Should Always Do */}
        <Card className="border-slate-200 bg-white mb-12" data-testid="host-best-practices">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <CardTitle className="font-heading text-xl text-slate-900">
                  What Hosts Should Always Do
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Follow these best practices to maximise your protection
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {hostBestPractices.map((practice, idx) => {
                const IconComponent = practice.icon;
                return (
                  <div 
                    key={idx}
                    className="p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#004B8D]/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-4 h-4 text-[#004B8D]" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 text-sm mb-1">
                          {practice.title}
                        </h4>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {practice.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Important Notice */}
        <Card className="border-amber-200 bg-amber-50/50" data-testid="important-notice">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-amber-900 mb-2">Important Information</h4>
                <p className="text-sm text-amber-800 leading-relaxed mb-2">
                  HireHub Protection provides tools and workflows to help hosts document trips and manage disputes. 
                  Hosts are responsible for ensuring their own insurance coverage is valid for peer-to-peer vehicle rental. 
                  HireHub does not provide insurance coverage.
                </p>
                <p className="text-sm text-amber-700 leading-relaxed font-medium">
                  All claims are assessed using inspection data. Claim outcomes depend on evidence quality and platform policies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 mb-4">Quick Links</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/host/bookings">
              <Button variant="outline" size="sm">
                <Calendar className="w-4 h-4 mr-2" />
                View Bookings
              </Button>
            </Link>
            <Link to="/host/vehicles">
              <Button variant="outline" size="sm">
                <Car className="w-4 h-4 mr-2" />
                Manage Vehicles
              </Button>
            </Link>
            <Link to="/host/statements">
              <Button variant="outline" size="sm">
                <Receipt className="w-4 h-4 mr-2" />
                Check Payouts
              </Button>
            </Link>
            <Link to="/dashboard/agreements">
              <Button variant="outline" size="sm">
                <FileText className="w-4 h-4 mr-2" />
                My Agreements
              </Button>
            </Link>
            <Link to="/owners/insurance">
              <Button variant="outline" size="sm">
                <Shield className="w-4 h-4 mr-2" />
                Insurance Guide
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-slate-500">
            HireHub Protection is part of the HireHub Australia platform.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HireHubProtection;
